/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { EditorType } from '@ckeditor/ckeditor5-metadata';

import type { CapabilityParsed, FeatureParsed, PluginParsed, PluginSelectionMode } from '../../components/PluginSelection/types';
import type { Capability, Feature } from '../../constants/capabilities';
import type { EditorPlugins } from '../../types/EditorPlugins';
import type { ViewPlugin } from '../../types/ViewPlugin';
import { findConflictingFeatures } from '../findConflictingFeatures/findConflictingFeatures';
import { findConflictingPlugins } from '../findConflictingPlugins/findConflictingPlugins';
import { getDependantFeatures } from '../getDependantFeatures/getDependantFeatures';
import { getDependantPlugins } from '../getDependantPlugins/getDependantPlugins';
import { getPluginByClassName } from '../getPluginByClassName/getPluginByClassName';
import { hasGroupConflict } from '../hasGroupConflict/hasGroupConflict';
import { hasPluginConflict } from '../hasPluginConflict/hasPluginConflict';
import { isGroupDisabled } from '../isGroupDisabled/isGroupDisabled';
import { isGroupFiltered } from '../isGroupFiltered/isGroupFiltered';
import { isGroupPremium } from '../isGroupPremium/isGroupPremium';
import { isGroupSelected } from '../isGroupSelected/isGroupSelected';
import { isGroupSelectedIndeterminate } from '../isGroupSelectedIndeterminate/isGroupSelectedIndeterminate';
import { isGroupVirtual } from '../isGroupVirtual/isGroupVirtual';
import { isPluginDisabled } from '../isPluginDisabled/isPluginDisabled';
import { isPluginSelected } from '../isPluginSelected/isPluginSelected';
import { isTextMatchedBySearchQuery } from '../isTextMatchedBySearchQuery/isTextMatchedBySearchQuery';

export type GetCapabilitiesForViewOptions = {
	editorType: EditorType;
	capabilities: ReadonlyArray<Capability>;
	editorPlugins: EditorPlugins;
	searchQuery: string;
	view: PluginSelectionMode;
};

export function getCapabilitiesForView( {
	editorType,
	capabilities,
	editorPlugins,
	searchQuery,
	view
}: GetCapabilitiesForViewOptions ): Array<CapabilityParsed> {
	return capabilities
		.filter( capability => capability.features.length > 0 )
		.map( capability => {
			const isMatchedBySearchQuery = isCapabilityMatchedBySearchQuery( { capability, searchQuery, view } );

			const capabilityFeatures = getFeaturesForView( {
				editorType,
				capability,
				capabilities,
				editorPlugins,
				searchQuery,
				isCapabilityMatchedBySearchQuery: isMatchedBySearchQuery,
				view
			} );

			return {
				...capability,
				id: capability.name.replaceAll( ' ', '' ),
				features: capabilityFeatures,
				plugins: capabilityFeatures.flatMap( feature => feature.plugins ),
				isVirtual: isGroupVirtual( capabilityFeatures ),
				isFiltered: isGroupFiltered( capabilityFeatures ),
				isSelected: isGroupSelected( capabilityFeatures, capability.isSingleFeatureChoice ),
				isSelectedIndeterminate: isGroupSelectedIndeterminate( capabilityFeatures, capability.isSingleFeatureChoice ),
				hasConflict: hasGroupConflict( capabilityFeatures ),
				isPremium: isGroupPremium( capabilityFeatures ),
				isDisabled: isGroupDisabled( capabilityFeatures )
			};
		} );
}

type GetFeaturesForViewOptions = {
	editorType: EditorType;
	capability: Capability;
	capabilities: ReadonlyArray<Capability>;
	editorPlugins: EditorPlugins;
	searchQuery: string;
	isCapabilityMatchedBySearchQuery: boolean;
	view: PluginSelectionMode;
};

function getFeaturesForView( {
	editorType,
	capability,
	capabilities,
	editorPlugins,
	searchQuery,
	isCapabilityMatchedBySearchQuery,
	view
}: GetFeaturesForViewOptions ): Array<FeatureParsed> {
	const { features } = capability;

	return features
		.filter( feature => feature.plugins.length > 0 )
		.map( feature => {
			const isGroupSingleChoice = capability.isSingleFeatureChoice || feature.isSinglePluginChoice;
			const isMatchedBySearchQuery = isFeatureMatchedBySearchQuery( {
				feature,
				searchQuery,
				isCapabilityMatchedBySearchQuery,
				view
			} );

			const featurePlugins = getPlugins( {
				capabilities,
				editorType,
				plugins: feature.plugins as EditorPlugins,
				editorPlugins,
				searchQuery,
				isFeatureMatchedBySearchQuery: isMatchedBySearchQuery,
				view
			} );

			return {
				...feature,
				id: feature.name.replaceAll( ' ', '' ),
				plugins: featurePlugins,
				isVirtual: isGroupVirtual( featurePlugins ),
				isFiltered: isGroupFiltered( featurePlugins ),
				isSelected: isGroupSelected( featurePlugins, isGroupSingleChoice ),
				isSelectedIndeterminate: isGroupSelectedIndeterminate( featurePlugins, isGroupSingleChoice ),
				hasConflict: hasGroupConflict( featurePlugins ),
				conflictingFeatures: findConflictingFeatures( featurePlugins, editorPlugins ),
				isPremium: isGroupPremium( featurePlugins ),
				isSinglePluginChoice: feature.isSinglePluginChoice,
				defaultPlugin: feature.defaultPlugin,
				isDisabled: isGroupDisabled( featurePlugins ),
				dependantFeatures: getDependantFeatures( { feature, featurePlugins, capabilities } )
			};
		} );
}

type GetPluginsOptions = {
	capabilities: ReadonlyArray<Capability>;
	editorType: EditorType;
	plugins: EditorPlugins;
	editorPlugins: EditorPlugins;
	searchQuery: string;
	isFeatureMatchedBySearchQuery: boolean;
	view: PluginSelectionMode;
};

function getPlugins( {
	capabilities,
	editorType,
	plugins,
	editorPlugins,
	searchQuery,
	isFeatureMatchedBySearchQuery,
	view
}: GetPluginsOptions ): Array<PluginParsed> {
	return plugins
		.map( getPluginByClassName )
		.filter( plugin => !plugin.hidden )
		.map( plugin => {
			return {
				...plugin,
				isVirtual: !!plugin.virtual,
				isFiltered: !isPluginMatchedBySearchQuery( { plugin, searchQuery, isFeatureMatchedBySearchQuery, view } ),
				isSelected: isPluginSelected( plugin, editorPlugins ),
				hasConflict: hasPluginConflict( plugin, editorPlugins ),
				conflictingPlugins: findConflictingPlugins( editorPlugins, [ plugin.className ] ),
				isPremium: !!plugin.premium,
				isDisabled: isPluginDisabled( plugin, editorType ),
				dependantPlugins: getDependantPlugins( { capabilities, plugin } )
			};
		} );
}

type IsCapabilityMatchedBySearchQueryOptions = {
	capability: Capability;
	searchQuery: string;
	view: PluginSelectionMode;
};

function isCapabilityMatchedBySearchQuery( options: IsCapabilityMatchedBySearchQueryOptions ) {
	const { capability, searchQuery, view } = options;

	const isNameMatchedBySearchQuery = isTextMatchedBySearchQuery( capability.name, searchQuery );
	const isDescriptionMatchedBySearchQuery = isTextMatchedBySearchQuery( capability.description, searchQuery );

	return view === 'basic' ?
		isNameMatchedBySearchQuery || isDescriptionMatchedBySearchQuery :
		false;
}

type IsFeatureMatchedBySearchQueryOptions = {
	feature: Feature;
	searchQuery: string;
	isCapabilityMatchedBySearchQuery: boolean;
	view: PluginSelectionMode;
};

function isFeatureMatchedBySearchQuery( options: IsFeatureMatchedBySearchQueryOptions ) {
	const { feature, searchQuery, isCapabilityMatchedBySearchQuery, view } = options;

	const isNameMatchedBySearchQuery = isTextMatchedBySearchQuery( feature.name, searchQuery );
	const isDescriptionMatchedBySearchQuery = isTextMatchedBySearchQuery( feature.description, searchQuery );

	return view === 'basic' ?
		isCapabilityMatchedBySearchQuery || isNameMatchedBySearchQuery || isDescriptionMatchedBySearchQuery :
		isCapabilityMatchedBySearchQuery || isNameMatchedBySearchQuery;
}

type IsPluginMatchedBySearchQueryOptions = {
	plugin: ViewPlugin;
	searchQuery: string;
	isFeatureMatchedBySearchQuery: boolean;
	view: PluginSelectionMode;
};

function isPluginMatchedBySearchQuery( options: IsPluginMatchedBySearchQueryOptions ) {
	const { plugin, searchQuery, isFeatureMatchedBySearchQuery, view } = options;

	const isNameMatchedBySearchQuery = isTextMatchedBySearchQuery( plugin.className, searchQuery );

	return view === 'basic' ?
		isFeatureMatchedBySearchQuery :
		isFeatureMatchedBySearchQuery || isNameMatchedBySearchQuery;
}

